import type { PrepOrder } from '@morales/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { StockNav } from '../components/StockNav';
import { Empty, QueryView } from '../components/ui';
import { addDays, formatDate, isIsoDate, todayIso } from '../format';
import { useLocale } from '../i18n';
import { buildCard, fq } from '../prep/card';
import { buildPull, buildTickets } from '../prep/tickets';
import { qk } from '../queryClient';

const qtyOf = (o: PrepOrder) => o.lines.reduce((a, l) => a + l.qty, 0);
/** to print by default: an order with something to pull that is not practice */
const printable = (o: PrepOrder) => o.lines.length > 0 && !o.test;

/**
 * The prep card (decision 179), as the Sheets' Prep.gs printed it: the invoices dated these days, ticked, on one letter
 * sheet folded in four for the order fillers (TOTALES first, then each order). JL: "this card summarizes the order and
 * then we print it out so that our order fillers have a quick reference guide for what they need to load".
 */
export function PrepPage() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const fromRaw = params.get('from');
  const toRaw = params.get('to');
  const today = todayIso();
  const from = isIsoDate(fromRaw) ? fromRaw : today;
  const to = isIsoDate(toRaw) && toRaw >= from ? toRaw : from;
  const list = useQuery({ queryKey: qk.prep(from, to), queryFn: ({ signal }) => api.prep(from, to, signal) });
  // null: the default (everything with lines, not practice); a set once a box is touched
  const [sel, setSel] = useState<Set<string> | null>(null);
  const [status, setStatus] = useState<{ text: string; bad?: boolean } | null>(null);
  const printRef = useRef<HTMLDivElement>(null);
  /** a print still open: finish it (printed again: it went to the printer, so it is marked) or undo it (the page left) */
  const pendingRef = useRef<{ finish: () => void; cleanup: () => void } | null>(null);
  useEffect(() => () => pendingRef.current?.cleanup(), []);
  useEffect(() => {
    setSel(null);
    setStatus(null);
  }, [from, to]);

  const orders = list.data?.orders ?? [];
  const selected = sel ?? new Set(orders.filter(printable).map((o) => o.invoiceId));
  const picked = orders.filter((o) => selected.has(o.invoiceId) && o.lines.length > 0);
  /** both ends at once (two separate updates would lose one), Back keeping the days */
  const setRange = (f: string, tt: string) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.set('from', f);
        out.set('to', tt < f ? f : tt);
        return out;
      },
      { replace: true },
    );
  const setDay = (d: string) => setRange(d, d);
  const toggle = (id: string, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(id);
    else next.delete(id);
    setSel(next);
  };

  type Kind = 'card' | 'tickets' | 'pull';
  /** Lay out what is printed, print it alone on its paper, and mark it (the card and the tickets) once printing is over. */
  function printWhat(kind: Kind) {
    const out = printRef.current;
    if (!out || picked.length === 0) return;
    const orders = picked.map((o) => ({ key: o.invoiceId, customer: o.customerName, lines: o.lines, invoiceNo: o.invoiceNo, date: o.invoiceDate }));
    const zebra = {
      totals: t('prepTotals'), cont: t('prepCont'), pulled: t('prepPulled'), checked: t('prepChecked'),
      linesQty: t('prepLinesQty'), lineQty: t('prepLineQty'),
      orders: picked.length === 1 ? t('prepOrders1') : t('prepOrdersN'), items: t('prepItemsN'), item: t('prepItems1'),
    };
    let said: string;
    if (kind === 'card') {
      const r = buildCard(out, orders, { totals: t('prepTotals'), cont: t('prepCont') });
      said = [
        t('prepFitted').replace('{orders}', String(picked.length)).replace('{panels}', String(r.panels)).replace('{min}', String(r.minPt)).replace('{max}', String(r.maxPt)),
        r.sides === 1 ? t('prepOneSide') : r.sheets > 1 ? `${t('prepTwoSided')}, ${t('prepSheets').replace('{n}', String(r.sheets))}` : t('prepTwoSided'),
        ...(r.spilled ? [t('prepSpilled')] : []),
      ].join(' · ');
    } else {
      const n = kind === 'tickets' ? buildTickets(out, orders, zebra, (d) => formatDate(d, intlLocale)) : buildPull(out, orders, zebra, from === to ? formatDate(from, intlLocale) : `${formatDate(from, intlLocale)} – ${formatDate(to, intlLocale)}`);
      said = (n === 1 ? t('prepLabel1') : t('prepLabels')).replace('{n}', String(n));
    }
    // it prints alone, on its paper (letter for the card, a 4 × 6 label for the Zebra). Phones return from print()
    // before the dialog is done, so the page's own print rules come back, and the invoices are marked printed, only
    // once the browser says printing is over
    pendingRef.current?.finish();
    const page = document.createElement('style');
    page.textContent = kind === 'card' ? '@page { size: letter portrait; margin: 0.25in; }' : '@page { size: 4in 6in; margin: 0; }';
    document.head.appendChild(page);
    document.body.classList.add('prep-printing');
    const ids = picked.map((o) => ({ invoiceId: o.invoiceId, hash: o.hash }));
    const cleanup = () => {
      window.removeEventListener('afterprint', after);
      document.body.classList.remove('prep-printing');
      page.remove();
      pendingRef.current = null;
    };
    const after = () => {
      cleanup();
      // TOTALES on labels is not an order's print: nothing to mark (as in the Sheets)
      if (kind === 'pull') setStatus({ text: `${said}. ${t('prepSent')}` });
      else void mark(kind, ids, said);
    };
    pendingRef.current = { finish: after, cleanup };
    window.addEventListener('afterprint', after);
    setStatus({ text: said });
    window.print();
  }

  async function mark(kind: 'card' | 'tickets', prints: { invoiceId: string; hash: string }[], said: string) {
    try {
      await api.prepPrinted({ kind, prints });
      await qc.invalidateQueries({ queryKey: ['stock', 'prep'] });
      setStatus({ text: `${said}. ${t('prepMarked')}` });
    } catch (e) {
      setStatus({ text: `${t('prepMarkFailed')} ${e instanceof Error ? e.message : String(e)}`, bad: true });
    }
  }

  const when = (iso: string) => new Intl.DateTimeFormat(intlLocale, { weekday: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'America/Denver' }).format(new Date(iso));
  const qtyPicked = picked.reduce((a, o) => a + qtyOf(o), 0);

  return (
    <div className="stack">
      <section className="stack-sm">
        <h1>{t('prepTitle')}</h1>
        <StockNav />
        <p className="muted">{t('prepHint')}</p>
      </section>
      <div className="toolbar" role="group" aria-label={t('date')}>
        <label className="prep-date">
          {t('prepFrom')}{' '}
          <input type="date" value={from} onChange={(e) => isIsoDate(e.target.value) && setRange(e.target.value, to)} />
        </label>
        <label className="prep-date">
          {t('prepTo')}{' '}
          <input type="date" value={to} min={from} onChange={(e) => isIsoDate(e.target.value) && setRange(from, e.target.value)} />
        </label>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDay(addDays(today, -1))}>
          {t('prepYesterday')}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDay(today)}>
          {t('prepToday')}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDay(addDays(today, 1))}>
          {t('prepTomorrow')}
        </button>
      </div>
      <QueryView query={list}>
        {() =>
          orders.length === 0 ? (
            <Empty>{t('prepEmpty')}</Empty>
          ) : (
            <section className="card stack-sm">
              <div className="toolbar">
                <strong data-testid="prep-count">
                  {t('prepCount').replace('{n}', String(picked.length)).replace('{total}', String(orders.length)).replace('{qty}', fq(qtyPicked))}
                </strong>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSel(new Set(orders.filter((o) => o.lines.length).map((o) => o.invoiceId)))}>
                  {t('prepAll')}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSel(new Set())}>
                  {t('prepNone')}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSel(new Set(orders.filter((o) => printable(o) && ((!o.cardPrintedAt && !o.ticketsPrintedAt) || o.changed)).map((o) => o.invoiceId)))}>
                  {t('prepNotPrinted')}
                </button>
              </div>
              <div className="table-wrap">
                <table className="prep-list data-table">
                  <thead>
                    <tr>
                      <th aria-label={t('print')} />
                      <th>{t('prepCustomer')}</th>
                      <th className="wide">{t('prepInvoiceNo')}</th>
                      <th className="wide">{t('date')}</th>
                      <th className="n wide">{t('lines')}</th>
                      <th className="n">{t('qty')}</th>
                      <th>{t('prepStatus')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => {
                      const on = selected.has(o.invoiceId) && o.lines.length > 0;
                      return (
                        <tr key={o.invoiceId} className={on ? undefined : 'off'}>
                          <td>
                            <input
                              type="checkbox"
                              aria-label={`${o.customerName} ${o.invoiceNo}`}
                              checked={on}
                              disabled={o.lines.length === 0}
                              onChange={(e) => toggle(o.invoiceId, e.target.checked)}
                            />
                          </td>
                          <td>
                            <strong>{o.customerName}</strong>
                            {o.test ? <span className="prep-badge chg"> {t('prepTest')}</span> : null}
                          </td>
                          <td className="wide">{o.invoiceNo}</td>
                          <td className="wide">{formatDate(o.invoiceDate, intlLocale)}</td>
                          <td className="n wide">{o.lines.length}</td>
                          <td className="n">{fq(qtyOf(o))}</td>
                          <td>
                            {o.changed ? <span className="prep-badge chg">{t('prepChanged')}</span> : null}
                            {/* each print on its own: the one out of date is red, so a fresh ticket never hides an old card */}
                            {o.cardPrintedAt ? <span className={o.cardChanged ? 'prep-badge chg' : 'prep-badge'}>{t('prepPrintedAt').replace('{when}', when(o.cardPrintedAt))}</span> : null}
                            {o.ticketsPrintedAt ? <span className={o.ticketsChanged ? 'prep-badge chg' : 'prep-badge'}>{t('prepTicketAt').replace('{when}', when(o.ticketsPrintedAt))}</span> : null}
                            {!o.cardPrintedAt && !o.ticketsPrintedAt ? <span className="prep-badge new">{t('prepNew')}</span> : null}
                            {o.lines.length === 0 ? <span className="muted"> {t('prepNoLines')}</span> : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="toolbar">
                <button type="button" className="btn btn-primary" disabled={picked.length === 0} onClick={() => printWhat('card')}>
                  {t('prepPrint')}
                </button>
                <span className="muted">{t('prepPrintHint')}</span>
              </div>
              <div className="toolbar">
                <button type="button" className="btn btn-primary" disabled={picked.length === 0} onClick={() => printWhat('tickets')}>
                  {t('prepTickets')}
                </button>
                <button type="button" className="btn btn-secondary" disabled={picked.length === 0} onClick={() => printWhat('pull')}>
                  {t('prepPullZebra')}
                </button>
                <span className="muted">{t('prepZebraHint')}</span>
              </div>
              {status ? (
                <p className={status.bad ? 'warn-band' : 'note'} role="status" data-testid="prep-status">
                  {status.text}
                </p>
              ) : null}
            </section>
          )
        }
      </QueryView>
      {createPortal(<div className="prep-print" ref={printRef} aria-hidden="true" />, document.body)}
    </div>
  );
}
